import React, { useState, useEffect, useCallback } from 'react';
import { Teacher } from '../../types';
import { X, Plus, Trash2, AlertCircle, Eye, EyeOff, CheckCircle, Upload, Image as ImageIcon, RefreshCw, BookOpen, Users, GraduationCap, MapPin, DollarSign } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { Subject, Class } from '../../utils/subjectsClasses';
import SubjectModal from '../ui/SubjectModal';
import ClassModal from '../ui/ClassModal';
import { uploadTeacherImage, deleteTeacherImage } from '../../services';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

interface TeacherFormProps {
    teacher?: Teacher;
    onSubmit: (teacher: Omit<Teacher, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    loading?: boolean;
}

const TeacherForm: React.FC<TeacherFormProps> = ({ teacher, onSubmit, onCancel, loading = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkingTeacherId, setCheckingTeacherId] = useState(false);
    const [teacherIdError, setTeacherIdError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const { showError, showSuccess } = useToast();
    const [teacherIdValid, setTeacherIdValid] = useState(false);
    const [emailValid, setEmailValid] = useState(false);
    const [showSubjectModal, setShowSubjectModal] = useState(false);
    const [showClassModal, setShowClassModal] = useState(false);
    const [selectedSubjects, setSelectedSubjects] = useState<Subject[]>([]);
    const [selectedClasses, setSelectedClasses] = useState<Class[]>([]);
    const [newPreviousSchool, setNewPreviousSchool] = useState({ schoolName: '', position: '', duration: '' });

    const [formData, setFormData] = useState({
        teacherId: teacher?.teacherId || '',
        password: '',
        confirmPassword: '',
        firstName: teacher?.firstName || '',
        lastName: teacher?.lastName || '',
        email: teacher?.email || '',
        phone: teacher?.phone || '',
        dateOfBirth: teacher?.dateOfBirth
            ? (teacher.dateOfBirth instanceof Date
                ? teacher.dateOfBirth.toISOString().split('T')[0]
                : new Date(teacher.dateOfBirth).toISOString().split('T')[0])
            : '',
        gender: teacher?.gender || 'male',
        cnic: teacher?.cnic || '',
        profileImage: teacher?.profileImage || '',
        selectedImageFile: null as File | null,
        address: {
            street: teacher?.address?.street || '',
            city: teacher?.address?.city || '',
            state: teacher?.address?.state || '',
            zipCode: teacher?.address?.zipCode || '',
            country: teacher?.address?.country || '',
        },
        qualification: {
            degree: teacher?.qualification?.degree || '',
            institution: teacher?.qualification?.institution || '',
            yearOfPassing: teacher?.qualification?.yearOfPassing || new Date().getFullYear(),
            subjects: teacher?.qualification?.subjects || [],
        },
        experience: {
            totalYears: teacher?.experience?.totalYears || 0,
            previousSchools: teacher?.experience?.previousSchools || [],
        },
        currentPosition: {
            department: teacher?.currentPosition?.department || '',
            designation: teacher?.currentPosition?.designation || '',
            joiningDate: teacher?.currentPosition?.joiningDate
                ? (teacher.currentPosition.joiningDate instanceof Date
                    ? teacher.currentPosition.joiningDate.toISOString().split('T')[0]
                    : new Date(teacher.currentPosition.joiningDate).toISOString().split('T')[0])
                : '',
            salary: teacher?.currentPosition?.salary || 0,
            status: teacher?.currentPosition?.status || 'active',
            inactiveReason: teacher?.currentPosition?.inactiveReason || '',
        },
        subjects: teacher?.subjects || [],
        classes: teacher?.classes || [],
        periods: teacher?.periods || [],
        emergencyContact: {
            name: teacher?.emergencyContact?.name || '',
            relationship: teacher?.emergencyContact?.relationship || '',
            phone: teacher?.emergencyContact?.phone || '',
            email: teacher?.emergencyContact?.email || '',
        },
        bankDetails: {
            accountNumber: teacher?.bankDetails?.accountNumber || '',
            bankName: teacher?.bankDetails?.bankName || '',
            ifscCode: teacher?.bankDetails?.ifscCode || '',
            accountHolderName: teacher?.bankDetails?.accountHolderName || '',
        },
    });

    // Load draft if creating new teacher
    useEffect(() => {
        if (!teacher) {
            const savedDraft = localStorage.getItem('teacher_form_draft');
            if (savedDraft) {
                try {
                    const parsed = JSON.parse(savedDraft);
                    setFormData(prev => ({
                        ...prev,
                        ...parsed,
                        password: '',
                        confirmPassword: '',
                        selectedImageFile: null
                    }));
                    if (parsed.profileImage) setImagePreview(parsed.profileImage);
                    showSuccess('Draft Loaded', 'Your unsaved teacher form draft has been loaded.');
                } catch (e) {
                    console.error('Error parsing draft:', e);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Save draft in local storage when formData changes
    useEffect(() => {
        if (!teacher) {
            const { password, confirmPassword, selectedImageFile, ...draftData } = formData;
            const isDirty = Object.entries(draftData).some(([_, val]) => {
                if (typeof val === 'string') return val.trim() !== '';
                if (Array.isArray(val)) return val.length > 0;
                if (typeof val === 'object' && val !== null) {
                    return Object.values(val).some(innerVal => {
                        if (typeof innerVal === 'string') return innerVal.trim() !== '';
                        if (typeof innerVal === 'number') return innerVal !== 0;
                        return false;
                    });
                }
                return false;
            });
            if (isDirty) localStorage.setItem('teacher_form_draft', JSON.stringify(draftData));
        }
    }, [formData, teacher]);

    // Auto-validate Teacher ID when user types (with debounce)
    useEffect(() => {
        if (!teacher && formData.teacherId.trim()) {
            const timer = setTimeout(() => checkTeacherIdUnique(formData.teacherId), 500);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.teacherId]);

    // Auto-generate email when first/last name changes
    useEffect(() => {
        if (!teacher && formData.firstName.trim()) {
            const timer = setTimeout(() => autoGenerateEmail(formData.firstName, formData.lastName), 600);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.firstName, formData.lastName, teacher]);

    // Auto-set password based on first name
    useEffect(() => {
        if (!teacher && formData.firstName.trim()) {
            const clean = formData.firstName.trim();
            const capitalized = clean.charAt(0).toUpperCase() + clean.slice(1);
            setFormData(prev => ({ ...prev, password: `${capitalized}123!`, confirmPassword: `${capitalized}123!` }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.firstName, teacher]);

    const autoGenerateEmail = useCallback(async (firstNameVal: string, lastNameVal: string) => {
        const cleanFirst = firstNameVal.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        const cleanLast = lastNameVal.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!cleanFirst) return;
        setCheckingEmail(true);
        try {
            const teachersRef = collection(db, 'teachers');
            let isUnique = false;
            let candidate = '';
            let attempts = 0;
            while (!isUnique && attempts < 30) {
                attempts++;
                const random3Digits = Math.floor(100 + Math.random() * 900);
                candidate = `${cleanFirst}${cleanLast}${random3Digits}@gmail.com`;
                const q = query(teachersRef, where('email', '==', candidate));
                const snap = await getDocs(q);
                if (snap.empty) isUnique = true;
            }
            if (isUnique && candidate) {
                setFormData(prev => ({ ...prev, email: candidate }));
                setEmailError('');
                setEmailValid(true);
            }
        } catch (err) {
            console.error('Error generating unique email:', err);
        } finally {
            setCheckingEmail(false);
        }
    }, []);

    const checkTeacherIdUnique = async (teacherId: string) => {
        if (!teacherId.trim()) { setTeacherIdError(''); return true; }
        if (teacher && teacher.teacherId === teacherId) { setTeacherIdError(''); return true; }
        try {
            setCheckingTeacherId(true);
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, where('teacherId', '==', teacherId.trim()));
            const snap = await getDocs(q);
            if (!snap.empty) { setTeacherIdError('This Teacher ID already exists.'); setTeacherIdValid(false); return false; }
            setTeacherIdError(''); setTeacherIdValid(true); return true;
        } catch { setTeacherIdError('Error validating Teacher ID.'); setTeacherIdValid(false); return false; }
        finally { setCheckingTeacherId(false); }
    };

    const checkEmailUnique = async (email: string) => {
        if (!email.trim()) { setEmailError(''); return true; }
        if (teacher && teacher.email === email) { setEmailError(''); return true; }
        try {
            setCheckingEmail(true);
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, where('email', '==', email.trim().toLowerCase()));
            const snap = await getDocs(q);
            if (!snap.empty) { setEmailError('This email is already registered.'); setEmailValid(false); return false; }
            setEmailError(''); setEmailValid(true); return true;
        } catch { setEmailError('Error validating email.'); setEmailValid(false); return false; }
        finally { setCheckingEmail(false); }
    };

    const handleImageUpload = (file: File) => {
        setFormData(prev => ({ ...prev, selectedImageFile: file }));
        setImagePreview(URL.createObjectURL(file));
        showSuccess('Success', 'Image selected! Will be uploaded on submit.');
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { showError('Invalid File', 'Please select an image file.'); return; }
        if (file.size > 5 * 1024 * 1024) { showError('File Too Large', 'Please select an image smaller than 5MB.'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreview(ev.target?.result as string);
        reader.readAsDataURL(file);
        handleImageUpload(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormError(null);
        if (name === 'teacherId') { setTeacherIdError(''); setTeacherIdValid(false); }
        if (name === 'email') { setEmailError(''); setEmailValid(false); }
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({ ...prev, [parent]: { ...(prev[parent as keyof typeof prev] as any), [child]: value } }));
        } else {
            setFormData(prev => {
                const next = { ...prev, [name]: value };
                if (!teacher && name === 'firstName' && value.trim()) {
                    const capitalized = value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
                    next.password = `${capitalized}123!`;
                    next.confirmPassword = `${capitalized}123!`;
                }
                return next;
            });
        }
    };

    const handleSubjectSelect = (subjects: Subject[]) => {
        setSelectedSubjects(subjects);
        setFormData(prev => ({ ...prev, subjects: subjects.map(s => s.name) }));
    };

    const handleClassSelect = (classes: Class[]) => {
        setSelectedClasses(classes);
        setFormData(prev => ({ ...prev, classes: classes.map(c => `${c.grade} - ${c.section}`) }));
    };

    const addPreviousSchool = () => {
        if (newPreviousSchool.schoolName.trim() && newPreviousSchool.position.trim() && newPreviousSchool.duration.trim()) {
            setFormData(prev => ({
                ...prev,
                experience: { ...prev.experience, previousSchools: [...prev.experience.previousSchools, { ...newPreviousSchool }] }
            }));
            setNewPreviousSchool({ schoolName: '', position: '', duration: '' });
        }
    };

    const removePreviousSchool = (index: number) => {
        setFormData(prev => ({
            ...prev,
            experience: { ...prev.experience, previousSchools: prev.experience.previousSchools.filter((_, i) => i !== index) }
        }));
    };

    const validateStep = (stepNum: number): boolean => {
        if (stepNum === 1) {
            if (!formData.firstName.trim()) { showError('First Name Required', 'Please enter First Name.'); return false; }
            if (!formData.email.trim()) { showError('Email Required', 'Please enter an email address.'); return false; }
            if (emailError) { showError('Duplicate Email', emailError); return false; }
            if (!teacher) {
                if (!formData.teacherId.trim()) { showError('Teacher ID Required', 'Please enter a Teacher ID.'); return false; }
                if (teacherIdError) { showError('Duplicate Teacher ID', teacherIdError); return false; }
                if (!formData.password || formData.password.length < 6) { showError('Password Too Short', 'Password must be at least 6 characters.'); return false; }
                if (formData.password !== formData.confirmPassword) { showError('Passwords Mismatch', 'Passwords do not match.'); return false; }
            }
        }
        if (stepNum === 2) {
            if (!formData.currentPosition.department.trim()) { showError('Department Required', 'Please enter a Department.'); return false; }
            const salaryVal = Number(formData.currentPosition.salary);
            if (isNaN(salaryVal) || salaryVal <= 0) { showError('Salary Required', 'Please enter a valid Salary.'); return false; }
        }
        return true;
    };

    const handleStepClick = (targetStep: number) => {
        if (targetStep < currentStep) { setCurrentStep(targetStep); return; }
        for (let s = currentStep; s < targetStep; s++) {
            if (!validateStep(s)) return;
        }
        setCurrentStep(targetStep);
    };

    const handleResetForm = () => {
        if (window.confirm('Are you sure you want to reset the form and clear the draft?')) {
            localStorage.removeItem('teacher_form_draft');
            setImagePreview(null);
            setFormData({
                teacherId: '', password: '', confirmPassword: '', firstName: '', lastName: '',
                email: '', phone: '', dateOfBirth: '', gender: 'male', cnic: '', profileImage: '',
                selectedImageFile: null,
                address: { street: '', city: '', state: '', zipCode: '', country: '' },
                qualification: { degree: '', institution: '', yearOfPassing: new Date().getFullYear(), subjects: [] },
                experience: { totalYears: 0, previousSchools: [] },
                currentPosition: { department: '', designation: '', joiningDate: '', salary: 0, status: 'active', inactiveReason: '' },
                subjects: [], classes: [], periods: [],
                emergencyContact: { name: '', relationship: '', phone: '', email: '' },
                bankDetails: { accountNumber: '', bankName: '', ifscCode: '', accountHolderName: '' },
            });
            setCurrentStep(1);
            showSuccess('Form Reset', 'The form has been reset and the draft has been cleared.');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        if (!formData.teacherId.trim()) {
            const msg = 'Please enter a Teacher ID.'; setFormError(msg); showError('Teacher ID Required', msg); return;
        }
        const isTeacherIdUnique = await checkTeacherIdUnique(formData.teacherId);
        if (!isTeacherIdUnique) {
            const msg = 'This Teacher ID already exists.'; setFormError(msg); showError('Duplicate Teacher ID', msg); return;
        }
        if (!formData.firstName.trim()) {
            const msg = 'Please enter First Name.'; setFormError(msg); showError('First Name Required', msg); return;
        }
        if (!formData.email.trim()) {
            const msg = 'Please enter an email address.'; setFormError(msg); showError('Email Required', msg); return;
        }
        const isEmailUnique = await checkEmailUnique(formData.email);
        if (!isEmailUnique) {
            const msg = 'This email is already registered.'; setFormError(msg); showError('Duplicate Email', msg); return;
        }
        if (!teacher && (!formData.password || formData.password.length < 6)) {
            const msg = 'Password must be at least 6 characters.'; setFormError(msg); showError('Password Too Short', msg); return;
        }

        const extractPublicId = (url: string): string | null => {
            const match = url.match(/\/v\d+\/(.+)\./);
            return match ? match[1] : null;
        };

        let profileImageUrl = formData.profileImage;
        if (formData.selectedImageFile) {
            setUploadingImage(true);
            try {
                if (teacher && teacher.profileImage) {
                    const oldPublicId = extractPublicId(teacher.profileImage);
                    if (oldPublicId) {
                        try { await deleteTeacherImage(oldPublicId); } catch { /* ignore */ }
                    }
                }
                const result = await uploadTeacherImage(formData.selectedImageFile, formData.teacherId);
                profileImageUrl = result.secure_url;
            } catch {
                showError('Error', 'Failed to upload image. Please try again.');
                setUploadingImage(false);
                return;
            }
            setUploadingImage(false);
        }

        const teacherData = {
            ...formData,
            profileImage: profileImageUrl,
            dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : new Date(),
            currentPosition: {
                ...formData.currentPosition,
                joiningDate: formData.currentPosition.joiningDate ? new Date(formData.currentPosition.joiningDate) : new Date(),
                salary: Number(formData.currentPosition.salary),
            },
            experience: { ...formData.experience, totalYears: Number(formData.experience.totalYears) },
            qualification: { ...formData.qualification, yearOfPassing: Number(formData.qualification.yearOfPassing) },
            periods: teacher?.periods || [],
        };
        const { selectedImageFile, ...submittedData } = teacherData;
        localStorage.removeItem('teacher_form_draft');
        onSubmit(submittedData);
    };

    const inputClass = 'w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300';

    return (
        <Modal
            isOpen={true}
            onClose={onCancel}
            title={teacher ? 'Edit Teacher Details' : 'Add New Teacher'}
            size="xl"
            footer={
                <>
                    <Button type="button" variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
                    {!teacher && (
                        <Button
                            type="button" variant="secondary" size="sm" onClick={handleResetForm}
                            className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
                        >
                            Reset Draft
                        </Button>
                    )}
                    {currentStep > 1 && (
                        <Button type="button" variant="secondary" size="sm" onClick={() => setCurrentStep(prev => prev - 1)}>Back</Button>
                    )}
                    {currentStep < 4 ? (
                        <Button type="button" size="sm" onClick={() => handleStepClick(currentStep + 1)}>Next</Button>
                    ) : (
                        <Button
                            type="submit" form="teacher-form" size="sm"
                            loading={loading || uploadingImage || checkingTeacherId || checkingEmail}
                            disabled={loading || uploadingImage || checkingTeacherId || checkingEmail || !!teacherIdError || !!emailError}
                        >
                            {loading ? 'Saving...' : teacher ? 'Update Teacher' : 'Add Teacher'}
                        </Button>
                    )}
                </>
            }
        >
            {/* Loading Overlay */}
            {(loading || uploadingImage) && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        <div className="text-gray-700 font-medium">
                            {uploadingImage ? 'Uploading image...' : teacher ? 'Updating teacher...' : 'Creating teacher account...'}
                        </div>
                    </div>
                </div>
            )}

            {/* Error Banner */}
            {formError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3 text-red-800">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm font-medium">{formError}</div>
                    <button type="button" onClick={() => setFormError(null)} className="text-red-500 hover:text-red-700">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Stepper Header */}
            <div className="mb-6 bg-slate-50/60 p-4 rounded-2xl border border-slate-100/50 backdrop-blur-md">
                <div className="flex items-center justify-between max-w-3xl mx-auto">
                    {[
                        { step: 1, label: 'Basic Info' },
                        { step: 2, label: 'Professional' },
                        { step: 3, label: 'Subjects & Classes' },
                        { step: 4, label: 'Contact & Bank' }
                    ].map((s, idx) => (
                        <React.Fragment key={s.step}>
                            {idx > 0 && (
                                <div className={`flex-1 h-0.5 mx-2 transition-all duration-500 ${currentStep > s.step ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                            )}
                            <button
                                type="button"
                                onClick={() => handleStepClick(s.step)}
                                className="flex flex-col items-center focus:outline-none group"
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${
                                    currentStep === s.step
                                        ? 'bg-emerald-600 text-white ring-4 ring-emerald-50'
                                        : currentStep > s.step
                                            ? 'bg-emerald-100 text-emerald-800'
                                            : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {s.step}
                                </div>
                                <span className={`text-[10px] font-bold mt-1 transition-colors ${
                                    currentStep === s.step ? 'text-emerald-700' : 'text-gray-400 group-hover:text-gray-600'
                                }`}>
                                    {s.label}
                                </span>
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <form id="teacher-form" onSubmit={handleSubmit} autoComplete="off" className={`space-y-8 relative ${(loading || uploadingImage) ? 'pointer-events-none opacity-50' : ''}`}>
                {/* ── Step 1: Basic Info ── */}
                {currentStep === 1 && (
                    <Card className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <Users className="h-6 w-6 mr-3 text-emerald-600" />
                            Basic Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Teacher ID */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Teacher ID <span className="text-red-500">*</span>
                                    </label>
                                    {teacher && <span className="text-xs text-gray-400 font-normal">(Cannot be changed)</span>}
                                </div>
                                <div className="relative">
                                    <input
                                        type="text" name="teacherId" value={formData.teacherId}
                                        onChange={handleInputChange} placeholder="Enter unique teacher ID"
                                        required readOnly={!!teacher} disabled={checkingTeacherId} autoComplete="off"
                                        className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${teacher
                                            ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                            : teacherIdError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50`}
                                    />
                                    {!teacher && checkingTeacherId && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600" /></div>}
                                    {!teacher && !checkingTeacherId && teacherIdError && <div className="absolute right-3 top-1/2 -translate-y-1/2"><AlertCircle className="h-4 w-4 text-red-500" /></div>}
                                    {!teacher && !checkingTeacherId && !teacherIdError && teacherIdValid && formData.teacherId.trim() && <div className="absolute right-3 top-1/2 -translate-y-1/2"><CheckCircle className="h-4 w-4 text-green-500" /></div>}
                                </div>
                                {!teacher && teacherIdError && <p className="mt-1 text-sm text-red-600 flex items-center"><AlertCircle className="h-3 w-3 mr-1" />{teacherIdError}</p>}
                            </div>

                            {/* First Name */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">First Name <span className="text-red-500">*</span></label>
                                </div>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange}
                                    placeholder="e.g. Arham" required autoComplete="off" className={inputClass} />
                            </div>

                            {/* Last Name */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                                    <span className="text-xs text-gray-400">(Optional)</span>
                                </div>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange}
                                    placeholder="e.g. Khan" autoComplete="off" className={inputClass} />
                            </div>

                            {/* Email */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                                    {!teacher ? (
                                        <button type="button" onClick={() => autoGenerateEmail(formData.firstName, formData.lastName)}
                                            disabled={checkingEmail || !formData.firstName.trim()}
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 disabled:opacity-50 transition-colors">
                                            <RefreshCw className={`h-3 w-3 ${checkingEmail ? 'animate-spin' : ''}`} />
                                            Generate New Email
                                        </button>
                                    ) : (
                                        <span className="text-xs text-gray-400">(Cannot be changed)</span>
                                    )}
                                </div>
                                <div className="relative">
                                    <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                                        placeholder="Auto-generated email" required readOnly={!!teacher} disabled={checkingEmail} autoComplete="off"
                                        className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${teacher
                                            ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                            : emailError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50`}
                                    />
                                    {!teacher && checkingEmail && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600" /></div>}
                                    {!teacher && !checkingEmail && emailError && <div className="absolute right-3 top-1/2 -translate-y-1/2"><AlertCircle className="h-4 w-4 text-red-500" /></div>}
                                    {!teacher && !checkingEmail && !emailError && emailValid && formData.email.trim() && <div className="absolute right-3 top-1/2 -translate-y-1/2"><CheckCircle className="h-4 w-4 text-green-500" /></div>}
                                </div>
                                {!teacher && emailError && <p className="mt-1 text-sm text-red-600 flex items-center"><AlertCircle className="h-3 w-3 mr-1" />{emailError}</p>}
                            </div>

                            {/* Phone */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                                    <span className="text-xs text-gray-400">(Optional)</span>
                                </div>
                                <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} autoComplete="off" className={inputClass} />
                            </div>

                            {/* Date of Birth */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                                    <span className="text-xs text-gray-400">(Optional)</span>
                                </div>
                                <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} autoComplete="off" className={inputClass} />
                            </div>

                            {/* Gender */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                                </div>
                                <select name="gender" value={formData.gender} onChange={handleInputChange} className={inputClass}>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            {/* CNIC */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">CNIC</label>
                                    <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                                </div>
                                <input
                                    type="text"
                                    name="cnic"
                                    value={formData.cnic}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 12345-1234567-1"
                                    autoComplete="off"
                                    className={inputClass}
                                />
                            </div>

                            {/* Password fields (new teacher only) */}
                            {!teacher && (
                                <>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-700">Password <span className="text-red-500">*</span></label>
                                            <span className="text-xs text-emerald-600 font-semibold">
                                                Auto-set: {formData.firstName ? `${formData.firstName.trim().charAt(0).toUpperCase() + formData.firstName.trim().slice(1)}123!` : 'Name123!'}
                                            </span>
                                        </div>
                                        <div className="relative">
                                            <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password}
                                                onChange={handleInputChange} placeholder="Auto-set password" required={!teacher} autoComplete="new-password"
                                                className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300" />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-700">Confirm Password <span className="text-red-500">*</span></label>
                                        </div>
                                        <div className="relative">
                                            <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword}
                                                onChange={handleInputChange} placeholder="Confirm password" required={!teacher} autoComplete="new-password"
                                                className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300" />
                                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                                                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Profile Image */}
                            <div className="md:col-span-2 lg:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image</label>
                                <div className="flex items-center space-x-4 bg-gray-50/50 p-4 rounded-xl border border-dashed border-gray-200">
                                    <div className="flex-shrink-0">
                                        <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border">
                                            {imagePreview || formData.profileImage ? (
                                                <img src={imagePreview || formData.profileImage} alt="Profile Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <ImageIcon className="h-6 w-6 text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="teacher-profileImage" />
                                        <label htmlFor="teacher-profileImage"
                                            className="cursor-pointer inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-all font-medium">
                                            {uploadingImage ? (
                                                <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Uploading...</>
                                            ) : (
                                                <><Upload className="h-3.5 w-3.5 mr-1.5" />Upload Image</>
                                            )}
                                        </label>
                                        <p className="text-xs text-gray-400 mt-1">JPG, PNG or GIF (Max 5MB)</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                {/* ── Step 2: Professional ── */}
                {currentStep === 2 && (
                    <Card className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <GraduationCap className="h-6 w-6 mr-3 text-emerald-600" />
                            Professional Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Department <span className="text-red-500">*</span></label>
                                <input type="text" name="currentPosition.department" value={formData.currentPosition.department}
                                    onChange={handleInputChange} placeholder="e.g. Science" autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                                <input type="text" name="currentPosition.designation" value={formData.currentPosition.designation}
                                    onChange={handleInputChange} placeholder="e.g. Senior Teacher" autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Joining Date</label>
                                <input type="date" name="currentPosition.joiningDate" value={formData.currentPosition.joiningDate}
                                    onChange={handleInputChange} autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Salary (Rs.) <span className="text-red-500">*</span></label>
                                <input type="number" name="currentPosition.salary" value={formData.currentPosition.salary}
                                    onChange={handleInputChange} placeholder="e.g. 50000" min="0" autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <select name="currentPosition.status" value={formData.currentPosition.status} onChange={handleInputChange} className={inputClass}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="resigned">Resigned</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Total Experience (Years)</label>
                                <input type="number" name="experience.totalYears" value={formData.experience.totalYears}
                                    onChange={handleInputChange} placeholder="e.g. 5" min="0" autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Degree / Qualification</label>
                                <input type="text" name="qualification.degree" value={formData.qualification.degree}
                                    onChange={handleInputChange} placeholder="e.g. M.Sc Mathematics" autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Institution</label>
                                <input type="text" name="qualification.institution" value={formData.qualification.institution}
                                    onChange={handleInputChange} placeholder="e.g. University of Punjab" autoComplete="off" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Year of Passing</label>
                                <input type="number" name="qualification.yearOfPassing" value={formData.qualification.yearOfPassing}
                                    onChange={handleInputChange} placeholder="e.g. 2015" min="1950" max={new Date().getFullYear()} autoComplete="off" className={inputClass} />
                            </div>
                        </div>

                        {/* Previous Schools */}
                        <div className="mt-6">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                <BookOpen className="h-4 w-4 mr-2 text-emerald-600" />
                                Previous Schools
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                <input type="text" placeholder="School Name" value={newPreviousSchool.schoolName}
                                    onChange={e => setNewPreviousSchool(prev => ({ ...prev, schoolName: e.target.value }))}
                                    className={inputClass} />
                                <input type="text" placeholder="Position" value={newPreviousSchool.position}
                                    onChange={e => setNewPreviousSchool(prev => ({ ...prev, position: e.target.value }))}
                                    className={inputClass} />
                                <div className="flex gap-2">
                                    <input type="text" placeholder="Duration (e.g. 2 years)" value={newPreviousSchool.duration}
                                        onChange={e => setNewPreviousSchool(prev => ({ ...prev, duration: e.target.value }))}
                                        className={inputClass} />
                                    <button type="button" onClick={addPreviousSchool}
                                        className="shrink-0 px-3 py-2.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors">
                                        <Plus className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            {formData.experience.previousSchools.length > 0 && (
                                <div className="space-y-2">
                                    {formData.experience.previousSchools.map((school, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5 text-sm">
                                            <span className="font-medium text-gray-800">{school.schoolName}</span>
                                            <span className="text-gray-500">{school.position} • {school.duration}</span>
                                            <button type="button" onClick={() => removePreviousSchool(index)}
                                                className="text-red-400 hover:text-red-600 ml-3">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* ── Step 3: Subjects & Classes ── */}
                {currentStep === 3 && (
                    <Card className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <BookOpen className="h-6 w-6 mr-3 text-emerald-600" />
                            Subjects & Classes
                        </h3>
                        <div className="space-y-6">
                            {/* Subjects */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-gray-700">Assigned Subjects</h4>
                                    <button type="button" onClick={() => setShowSubjectModal(true)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors">
                                        <Plus className="h-3.5 w-3.5" /> Select Subjects
                                    </button>
                                </div>
                                {formData.subjects.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.subjects.map((subject, index) => (
                                            <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                                {subject}
                                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, subjects: prev.subjects.filter((_, i) => i !== index) }))}
                                                    className="text-blue-400 hover:text-blue-600">
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">No subjects selected yet.</p>
                                )}
                            </div>

                            {/* Classes */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-gray-700">Assigned Classes</h4>
                                    <button type="button" onClick={() => setShowClassModal(true)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors">
                                        <Plus className="h-3.5 w-3.5" /> Select Classes
                                    </button>
                                </div>
                                {formData.classes.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.classes.map((cls, index) => (
                                            <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 border border-purple-100 text-purple-800 text-xs font-medium rounded-full">
                                                {cls}
                                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, classes: prev.classes.filter((_, i) => i !== index) }))}
                                                    className="text-purple-400 hover:text-purple-600">
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">No classes selected yet.</p>
                                )}
                            </div>
                        </div>
                    </Card>
                )}

                {/* ── Step 4: Contact & Bank ── */}
                {currentStep === 4 && (
                    <>
                        {/* Address */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                                <MapPin className="h-6 w-6 mr-3 text-emerald-600" />
                                Address Information
                            </h3>
                            <p className="text-sm text-gray-500 mb-6">Optional — Fill if address details are available</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                                    <input type="text" name="address.street" value={formData.address.street} onChange={handleInputChange} className={inputClass} />
                                </div>
                                {[
                                    { label: 'City', name: 'address.city', value: formData.address.city },
                                    { label: 'State', name: 'address.state', value: formData.address.state },
                                    { label: 'ZIP Code', name: 'address.zipCode', value: formData.address.zipCode },
                                    { label: 'Country', name: 'address.country', value: formData.address.country },
                                ].map(field => (
                                    <div key={field.name}>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                                        <input type="text" name={field.name} value={field.value} onChange={handleInputChange} className={inputClass} />
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* Emergency Contact */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <Users className="h-6 w-6 mr-3 text-emerald-600" />
                                Emergency Contact
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[
                                    { label: 'Contact Name', name: 'emergencyContact.name', value: formData.emergencyContact.name },
                                    { label: 'Relationship', name: 'emergencyContact.relationship', value: formData.emergencyContact.relationship },
                                    { label: 'Phone', name: 'emergencyContact.phone', value: formData.emergencyContact.phone },
                                    { label: 'Email', name: 'emergencyContact.email', value: formData.emergencyContact.email },
                                ].map(field => (
                                    <div key={field.name}>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                                        <input type="text" name={field.name} value={field.value} onChange={handleInputChange} className={inputClass} />
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* Bank Details */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <DollarSign className="h-6 w-6 mr-3 text-emerald-600" />
                                Bank Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[
                                    { label: 'Account Number', name: 'bankDetails.accountNumber', value: formData.bankDetails.accountNumber },
                                    { label: 'Bank Name', name: 'bankDetails.bankName', value: formData.bankDetails.bankName },
                                    { label: 'IFSC / Swift Code', name: 'bankDetails.ifscCode', value: formData.bankDetails.ifscCode },
                                    { label: 'Account Holder Name', name: 'bankDetails.accountHolderName', value: formData.bankDetails.accountHolderName },
                                ].map(field => (
                                    <div key={field.name}>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                                        <input type="text" name={field.name} value={field.value} onChange={handleInputChange} className={inputClass} />
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </>
                )}
            </form>

            {/* Subject Modal */}
            {showSubjectModal && (
                <SubjectModal
                    isOpen={showSubjectModal}
                    selectedSubjects={selectedSubjects}
                    selectedClasses={selectedClasses}
                    onSelect={handleSubjectSelect}
                    onClose={() => setShowSubjectModal(false)}
                />
            )}
            {/* Class Modal */}
            {showClassModal && (
                <ClassModal
                    isOpen={showClassModal}
                    selectedClasses={selectedClasses}
                    onSelect={handleClassSelect}
                    onClose={() => setShowClassModal(false)}
                />
            )}
        </Modal>
    );
};

export default TeacherForm;
